import React, { useState } from 'react';
import {
  TouchableOpacity, Dimensions,
} from 'react-native';
import {
  Box,
  HStack,
  VStack,
  Text,
  useMedia,
  useToken,
  FlatList,
  View,
  Icon,
} from '@gluestack-ui/themed';
import { ChevronDown, Check } from 'lucide-react-native';
import Svg, { Path } from 'react-native-svg';
import { Level } from '../types';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface ModuleRoadmapPathProps {
  /** List of levels in the module */
  levels?: Level[];
  /** Primary brand color for the module */
  brandColor: string;
  /** Whether the module is already graded */
  isGraded: boolean;
  /** Whether the module is locked */
  isLocked: boolean;
}

/**
 * ModuleRoadmapPath displays a horizontal timeline of levels/objectives
 * within a module, showing progress and locked states.
 */
export const ModuleRoadmapPath: React.FC<ModuleRoadmapPathProps> = ({
  levels, brandColor, isGraded, isLocked,
}) => {
  const media = useMedia();
  const [expandedLevels, setExpandedLevels] = useState<Record<number, boolean>>({});
  // @ts-expect-error - statusSuccess is a semantic token
  const statusSuccess = useToken('colors', 'statusSuccess') || '#000000';
  // @ts-expect-error - borderLight is a semantic token
  const borderLight = useToken('colors', 'borderLight') || '#000000';

  const toggleLevel = (id: number): void => {
    setExpandedLevels((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Responsive sizing constants - decreased heights
  const cardWidth = media.lg ? 340 : media.md ? 280 : SCREEN_WIDTH * 0.85;
  const spacing = media.md ? 40 : 20;
  const cardHeight = media.lg ? 180 : media.md ? 160 : 140;

  return (
    <VStack space="xs" w="100%" mt={media.md ? '$2' : '$1'}>
      <View h={cardHeight + 20}>
        <FlatList
          horizontal
          data={levels}
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item) => `level-path-${(item as Level).id}`}
          py="$2"
          px="$1"
          renderItem={({ item, index }) => {
            const level = item as Level;
            const isExpanded = expandedLevels[level.id];
            return (
              <HStack alignItems="center">
                <TouchableOpacity activeOpacity={0.9} onPress={() => toggleLevel(level.id)}>
                  <VStack
                    p={media.md ? '$5' : '$4'}
                    bg={isGraded ? '$statusSuccessSubtle' : '$bgCard'}
                    borderRadius="$xl"
                    width={cardWidth}
                    height={isExpanded ? undefined : cardHeight}
                    minHeight={cardHeight}
                    borderWidth={1}
                    borderColor={isGraded ? '$statusSuccess' : '$borderLight'}
                  >
                    <HStack justifyContent="space-between" alignItems="center" mb="$0.5">
                      <Text
                        size="2xs"
                        fontWeight="bold"
                        color={isLocked ? '$textHint' : isGraded ? '$textOnSuccessSubtle' : brandColor}
                      >
                        LEVEL 0
                        {level.id}
                      </Text>
                      {isGraded ? (
                        <Icon as={Check} size="xs" color={statusSuccess} />
                      ) : (
                        !!isExpanded && (
                          <Icon as={ChevronDown} size="xs" color={brandColor} sx={{ transform: [{ rotate: '180deg' }] }} />
                        )
                      )}
                    </HStack>

                    <Box height={media.md ? 38 : 32} justifyContent="center" mb="$1.5">
                      <Text
                        size={media.md ? 'sm' : 'xs'}
                        fontWeight="900"
                        color={isGraded ? '$textOnSuccessSubtle' : '$textHeading'}
                        numberOfLines={isExpanded ? undefined : 2}
                        lineHeight={media.md ? '$sm' : '$xs'}
                      >
                        {level.title}
                      </Text>
                    </Box>

                    {!!level.objectives && (
                      <VStack space="xs">
                        {level.objectives.slice(0, isExpanded ? undefined : media.md ? 2 : 1).map((objective) => (
                          <HStack key={`objective-${objective}`} space="xs" alignItems="flex-start">
                            <Text size="2xs" mt={1} color={isGraded ? '$statusSuccess' : brandColor}>
                              •
                            </Text>
                            <Text
                              size="2xs"
                              flex={1}
                              numberOfLines={isExpanded ? undefined : 2}
                              color={isGraded ? '$textOnSuccessSubtle' : '$textBody'}
                            >
                              {objective}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </TouchableOpacity>
                {index < (levels?.length || 0) - 1 && (
                  // eslint-disable-next-line react-native/no-inline-styles
                  <View style={{ width: spacing, alignItems: 'center' }}>
                    <Svg width={spacing} height={2}>
                      <Path
                        d={`M 0 1 L ${spacing} 1`}
                        stroke={isLocked ? borderLight : isGraded ? statusSuccess : brandColor}
                        strokeWidth="2"
                        strokeDasharray="4 4"
                      />
                    </Svg>
                  </View>
                )}
              </HStack>
            );
          }}
        />
      </View>
    </VStack>
  );
};
